import {
  CommonWidgetUtils,
  EVENT_TYPE_TRANSACTION,
  HistogramApiHelper,
  WIDGET_DATA_STATISTIC_SUM,
  WIDGET_LEGACY_DATA_PATH_TOTAL_AMOUNT,
  WIDGET_TYPE_EVENT_TICKER,
  type WidgetType,
} from '@solinkcorp/vd-common';
import { type IEvent } from 'clients/PlatformApi';
import { type Draft } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import memoizeOne from 'memoize-one';
import { makeImmerReducer } from 'reducers/utils';
import { _debug } from 'utils/LoggingUtil';
import {
  type FetchEventsTickerWidgetDataDoneAction,
  type FetchRankingWidgetDataDoneAction,
  type FetchReportWidgetDataDoneAction,
  type GetTickerEventAction,
  type UpdateWidgetsAction,
} from './actions';
import {
  isStoredRankingWidget,
  isStoredReportWidget,
  type StoredWidget,
  type TickerData,
} from './types';

const MAX_EVENT_PROCESSED_QUEUE_SIZE = 40; // Events on the Ribbon
const MAX_EVENT_UNPROCESSED_QUEUE_SIZE = 200;

const isActionTypeMatchWidgetType = (actionType: string, widgetType: WidgetType) => {
  if (actionType === 'FETCH_REPORT_WIDGET_DATA' && CommonWidgetUtils.isReportWidget(widgetType)) {
    return true;
  }
  if (actionType === 'FETCH_RANKING_WIDGET_DATA' && CommonWidgetUtils.isRankingWidget(widgetType)) {
    return true;
  }
  if (actionType === 'FETCH_EVENTS_TICKER_WIDGET_DATA' && widgetType === WIDGET_TYPE_EVENT_TICKER) {
    return true;
  }
};

/**
 * Reducer which keeps track of the data for the widgets on the dashboard page.
 */
export type HomePageState = {
  widgets: StoredWidget[];
  previewWidget: StoredWidget | undefined;
  tickerData: TickerData;
  previewTickerData: TickerData;
};

const initialTickerDataState = {
  tickerTotal: 0,
  tickerTotalPrev: 0,
  tickerSummaryValue: 0,
  tickerSummaryValueTotal: 0,
  tickerSummaryValuePrev: 0,
  tickerEvents: [],
  tickerPollingData: [],
  tickerPollingEndTime: undefined,
  tickerConflictDict: {},
};

export const initialState: HomePageState = {
  widgets: [],
  previewWidget: undefined,
  tickerData: initialTickerDataState,
  previewTickerData: initialTickerDataState,
};

export const homePageReducer = makeImmerReducer(initialState, (draft, action) => {
  switch (action.type) {
    case 'UPDATE_WIDGETS':
      setWidgets(draft, action);
      break;

    case 'UPDATE_PREVIEW_WIDGET':
      draft.previewWidget = action.widget;
      break;

    case 'FETCH_REPORT_WIDGET_DATA':
      setWidgetFetchingStatus(draft, action);
      break;

    case 'FETCH_REPORT_WIDGET_DATA_DONE':
      setReportWidgetData(draft, action);
      break;

    case 'FETCH_REPORT_WIDGET_DATA_ERROR':
      setWidgetError(draft, action);
      break;

    case 'GET_TICKER_EVENT':
      getTickerEvent(draft, action);
      break;

    case 'FETCH_EVENTS_TICKER_WIDGET_DATA':
      setWidgetFetchingStatus(draft, action);
      break;

    case 'FETCH_EVENTS_TICKER_WIDGET_DATA_DONE':
      setEventsTickerPollingData(draft, action);
      break;

    case 'FETCH_EVENTS_TICKER_WIDGET_DATA_ERROR':
      setWidgetError(draft, action);
      break;

    case 'RESET_TICKER_WIDGET':
      resetTickerWidgetData(draft, action.isPreview);
      break;

    case 'FETCH_RANKING_WIDGET_DATA':
      setWidgetFetchingStatus(draft, action);
      break;

    case 'FETCH_RANKING_WIDGET_DATA_DONE':
      setRankingWidgetData(draft, action);
      break;

    case 'FETCH_RANKING_WIDGET_DATA_ERROR':
      setWidgetError(draft, action);
      break;
  }
});

const setWidgets = (state: Draft<HomePageState>, action: UpdateWidgetsAction) => {
  const { widgets } = action;
  const oldWidgetIndexLookup = getWidgetIndexLookup(state.widgets);

  const newWidgets: StoredWidget[] = widgets.map((widget) => {
    // Keep any additional properties from our widget.
    const storedIndex = oldWidgetIndexLookup[widget.id];
    const storedWidget = storedIndex !== undefined ? state.widgets[storedIndex] : undefined;

    let newWidget: StoredWidget;

    if (storedWidget?.widgetSettings.type === widget.widgetSettings.type) {
      newWidget = { ...storedWidget, ...widget } as StoredWidget;
    } else {
      newWidget = {
        ...widget,
        fetching: false,
        fetched: false,
      } as StoredWidget;
    }

    return newWidget;
  });

  state.widgets = newWidgets;
};

const setWidgetFetchingStatus = (
  state: Draft<HomePageState>,
  action: { id?: string; isPreview?: boolean; fetchDataParams: unknown; type: string },
) => {
  const widgetToUpdate = getWidgetToUpdate(state, action);
  widgetToUpdate.fetched = false;
  widgetToUpdate.fetching = true;
  widgetToUpdate.lastFetchDataParams = cloneDeep(action.fetchDataParams);

  /** When creating new chartable widget and the widget type change, the preview widget's dataSets needs to be reset. */
  if (
    (isStoredRankingWidget(widgetToUpdate) || isStoredReportWidget(widgetToUpdate)) &&
    !isActionTypeMatchWidgetType(action.type, widgetToUpdate.widgetSettings.type) &&
    widgetToUpdate.dataSets?.length
  ) {
    widgetToUpdate.dataSets = [];
  }
};

const setWidgetError = (
  state: Draft<HomePageState>,
  action: { id: string; isPreview: boolean },
) => {
  const widgetToUpdate = getWidgetToUpdate(state, action);
  widgetToUpdate.error = true;
  widgetToUpdate.fetched = true;
  widgetToUpdate.fetching = false;
};

const resetTickerWidgetData = (state: Draft<HomePageState>, isPreview: boolean) => {
  if (isPreview) {
    state.previewTickerData = initialTickerDataState;
  } else {
    state.tickerData = initialTickerDataState;
  }
  _debug('resetTickerWidget: ', state);
};

const getTickerEvent = (state: Draft<HomePageState>, { id, isPreview }: GetTickerEventAction) => {
  let tickerWidget;
  let widgetIndex;
  if (isPreview) {
    tickerWidget = state.previewWidget;
  } else {
    const widgetIndexLookup = getWidgetIndexLookup(state.widgets);
    widgetIndex = widgetIndexLookup[id];
    tickerWidget = state.widgets[widgetIndex];
  }
  if (!tickerWidget) {
    throw new Error(`Cannot find the ticker widget from index ${widgetIndex}`);
  }
  if (tickerWidget.widgetSettings.type !== 'ticker') {
    throw new Error(
      `Expected widget index ${id} to be ticker widget, but is ${tickerWidget.widgetSettings.type}`,
    );
  }

  const tickerDataToUpdate = getTickerDataToUpdate(state, isPreview);

  const dataPath = tickerWidget.widgetSettings.dataPaths[0];
  const dataStatistic = tickerWidget.widgetSettings.dataStatistic;
  const count = 1;
  // processed events means they are on the ribbon already
  // the most recently processed event in this queue will be
  // added to the back
  let processedEvents = tickerDataToUpdate.tickerEvents;
  // unprocessed events means they are waiting to be consumed
  // off this array from the front
  const unprocessedEvents = tickerDataToUpdate.tickerPollingData;
  const {
    tickerConflictDict: conflictDict,
    tickerSummaryValue: currentSummaryValue,
    tickerSummaryValueTotal: currentSummaryValueTotal,
    tickerTotal: currentTotalCount,
  } = tickerDataToUpdate;

  // Calculate new summary value
  let newSummaryValue = currentSummaryValue;

  // event pool should be FIFO, lower the index, the older the event
  const movedEvents: IEvent[] = [];
  let eventsToPopCount = count;
  if (unprocessedEvents.length < eventsToPopCount) {
    eventsToPopCount = unprocessedEvents.length;
    _debug(
      `getTickerEvent: asked to pop ${count} but pool has ${unprocessedEvents.length} available, limiting to this amount instead`,
    );
  }

  for (let i = 0; i < eventsToPopCount; i++) {
    const poppedEvent = unprocessedEvents.shift();
    if (poppedEvent) {
      movedEvents.push(poppedEvent);
    }
  }

  movedEvents.forEach((event) => {
    const { id } = event;
    if (!conflictDict[id]) {
      conflictDict[id] = true;
      processedEvents.unshift(event);

      const eventValue =
        dataPath === WIDGET_LEGACY_DATA_PATH_TOTAL_AMOUNT
          ? event.details?.['Total amount'] || 0
          : get(event, dataPath, 0);

      if (CommonWidgetUtils.isShowingSum(dataStatistic)) {
        newSummaryValue += eventValue;
      } else if (CommonWidgetUtils.isShowingAverage(dataStatistic)) {
        newSummaryValue = (currentSummaryValueTotal + eventValue) / (currentTotalCount + 1);
      } else {
        console.error('Ticker widget: invalid data statistic for ticker widget', dataStatistic);
      }
    } else {
      _debug(`Skipping this event because it was already processed: ${id}`, event);
    }
  });

  // Limit the size of the events in memory for the ribbon
  processedEvents = processedEvents.slice(0, MAX_EVENT_PROCESSED_QUEUE_SIZE);

  tickerDataToUpdate.tickerEvents = processedEvents;
  tickerDataToUpdate.tickerSummaryValuePrev = currentSummaryValue;
  tickerDataToUpdate.tickerSummaryValue = newSummaryValue;

  _debug('getTickerEvent - updated state: ', state);
};

const setEventsTickerPollingData = (
  state: Draft<HomePageState>,
  action: FetchEventsTickerWidgetDataDoneAction,
) => {
  const { buckets, events, sortOrder, total, dataPath, dataStatistic, summary, isPreview } = action;

  const tickerDataToUpdate = getTickerDataToUpdate(state, isPreview);

  // Current state values for events and total
  //// processed events means they are on the ribbon already
  //// the most recently processed event in this queue will be
  //// added to the back
  const processedEvents = tickerDataToUpdate.tickerEvents;
  //// unprocessed events means they are waiting to be consumed
  //// off this array from the front
  const unprocessedEvents = tickerDataToUpdate.tickerPollingData;
  const currentTotal = tickerDataToUpdate.tickerTotal;

  // Determine new events for state
  //// Sort events
  const sortedEvents = sortOrder === 'desc' ? events.reverse() : events;
  let newSortedEvents = [];
  let newEvents: IEvent[] = [];
  let lastEventEndTime;
  if (sortedEvents.length >= 1) {
    lastEventEndTime = sortedEvents[sortedEvents.length - 1].endTime;

    //// Case 1: Initial fetch of events, sorted events just get set as new state
    if (unprocessedEvents.length === 0 && processedEvents.length === 0) {
      newEvents = sortedEvents;
    }
    //// Case 2: Appending fetch of events
    else {
      // NOTE: If the `end` and `start` time of two event queries are the same, there will be
      // duplicate events. Adding 1 millisecond has no effect because search queries are
      // rounded to the nearest second for caching reasons.
      // https://github.com/SolinkCorp/platform-api/pull/245/commits/465d12f80e93b23ba7f5a7b1f07e64a72b1e1df7
      // Adding 1s which is the lowest unit that will matter to the query, but that will miss events
      // that happened between the two seconds, so we have no choice but to use the same `end`
      // and `start` values and remove any duplicates (which occur at start - index 0).

      // Find index of first event we do not already have (avoid duplicates)
      let indexOfFirstNewEvent = -1;
      for (let i = 0; i < sortedEvents.length; i++) {
        const eventId = sortedEvents[i].id;
        // find last event, either in the unprocessed queue or the processed queue
        // it should be the newest we had to date
        let lastEvent;
        if (unprocessedEvents.length > 0) {
          // last event in the unprocessed queue is the highest index
          lastEvent = unprocessedEvents[unprocessedEvents.length - 1];
        } else {
          // last event in the processed queue is lowest index
          lastEvent = processedEvents[0];
        }

        if (lastEvent && lastEvent.id !== eventId) {
          indexOfFirstNewEvent = i;
          break;
        }
      }
      if (indexOfFirstNewEvent !== -1) {
        newSortedEvents = sortedEvents.slice(indexOfFirstNewEvent);
        _debug('The count of new additional events should be: ', newSortedEvents.length);
        newEvents = unprocessedEvents.concat(newSortedEvents);
        newEvents = newEvents.slice(-MAX_EVENT_UNPROCESSED_QUEUE_SIZE);
      } else {
        _debug('No new events, do nothing!');
        return state;
      }
    }

    // Determine new total for state
    let newTotal;
    if (!unprocessedEvents.length && !processedEvents.length && currentTotal === 0) {
      // initialize total with the total from the response
      newTotal = total;
      _debug('Initializing the TOTAL to be: ', newTotal);
    } else {
      // increment total by incrementing existing total with new sorted events length
      newTotal = currentTotal + newSortedEvents.length;
      _debug('Incrementing the TOTAL to be: ', newTotal);
    }

    tickerDataToUpdate.tickerPollingData = newEvents;
    tickerDataToUpdate.tickerTotalPrev = currentTotal;
    tickerDataToUpdate.tickerTotal = newTotal;
    tickerDataToUpdate.tickerPollingEndTime = lastEventEndTime;
  }

  // Determine new summary value for state
  const currentSummaryValue = tickerDataToUpdate.tickerSummaryValue;
  let newSummaryValue = 0;
  if (dataPath === WIDGET_LEGACY_DATA_PATH_TOTAL_AMOUNT && buckets?.length) {
    buckets.forEach((bucket) => {
      const transaction = bucket.results.find((result) => result.type === EVENT_TYPE_TRANSACTION);
      if (transaction && !isNaN(transaction.totalAmountSum)) {
        newSummaryValue += transaction.totalAmountSum;
      }
    });
  } else if (summary?.length) {
    if (CommonWidgetUtils.isShowingAverage(dataStatistic)) {
      if (summary.length === 1) {
        newSummaryValue = summary[0]?.fieldStats?.[dataPath]?.[dataStatistic] || 0;
        const newSummaryValueTotal =
          summary[0]?.fieldStats?.[dataPath]?.[WIDGET_DATA_STATISTIC_SUM] || 0;
        tickerDataToUpdate.tickerSummaryValueTotal = newSummaryValueTotal;
      } else {
        let totalSum = 0;
        let totalCount = 0;
        summary.forEach((result) => {
          const fieldStats = result?.fieldStats?.[dataPath];
          if (fieldStats) {
            totalSum += fieldStats.sum ?? 0;
            totalCount += fieldStats.count ?? 0;
          }
        });

        tickerDataToUpdate.tickerSummaryValueTotal = totalSum;
        if (totalCount !== 0) {
          newSummaryValue = totalSum / totalCount;
        }
      }
    } else {
      summary.forEach((result) => {
        const summaryValue = result?.fieldStats?.[dataPath]?.[dataStatistic];
        if (summaryValue) {
          newSummaryValue += summaryValue;
        }
      });
    }
  }

  tickerDataToUpdate.tickerSummaryValuePrev = currentSummaryValue;
  tickerDataToUpdate.tickerSummaryValue = newSummaryValue;

  _debug('setEventsTickerPollingData - updated state: ', state);
};

const setRankingWidgetData = (
  state: Draft<HomePageState>,
  action: FetchRankingWidgetDataDoneAction,
) => {
  const { dataSets, masterLocationIds, rankBy } = action;

  const filteredDataSets = HistogramApiHelper.normalizeTopNData({
    dataSets,
    siteIds: masterLocationIds,
    rankBy,
  });

  const widgetToUpdate = getWidgetToUpdate(state, action);

  if (!isStoredRankingWidget(widgetToUpdate)) {
    throw new Error(`Expected ranking widget, got ${widgetToUpdate.widgetSettings.type}`);
  }

  widgetToUpdate.dataSets = filteredDataSets;
  widgetToUpdate.fetched = true;
  widgetToUpdate.fetching = false;
  widgetToUpdate.isEmpty = false;

  _debug('setRankingWidgetData - updated state: ', state);
};

const setReportWidgetData = (
  state: Draft<HomePageState>,
  action: FetchReportWidgetDataDoneAction,
) => {
  const {
    dataSets,
    dataSetTypes,
    histogramApiParams,
    locationCount,
    range,
    rangeRolling,
    reportTemplateIds,
    units,
  } = action;

  const { isEmpty, normalizedDataSets: processedDataSets } =
    HistogramApiHelper.normalizeHistogramDataSetsCollection({
      dataSetsCollection: dataSets,
      histogramApiParams,
      range,
      rangeRolling,
      units,
      locationCount,
    });

  const widgetToUpdate = getWidgetToUpdate(state, action);

  if (!isStoredReportWidget(widgetToUpdate)) {
    throw new Error(`Expected report widget, got ${widgetToUpdate.widgetSettings.type}`);
  }

  widgetToUpdate.dataSets = processedDataSets;
  widgetToUpdate.dataSetTypes = dataSetTypes;
  widgetToUpdate.error = undefined;
  widgetToUpdate.fetched = true;
  widgetToUpdate.fetching = false;
  widgetToUpdate.isEmpty = isEmpty;
  widgetToUpdate.range = range;
  widgetToUpdate.reportTemplateIds = reportTemplateIds;
};

/**
 * Returns a dictionary where keys are widget IDs and values are the index of the
 * widget in the `widgets` array.
 */
export const getWidgetIndexLookup = memoizeOne(
  (widgets: { id: string }[]): { [id: string]: number } => {
    const widgetIndexLookup: { [id: string]: number } = {};
    widgets.forEach((widget, index) => {
      widgetIndexLookup[widget.id] = index;
    });

    return widgetIndexLookup;
  },
);

function getWidgetToUpdate(
  state: Draft<HomePageState>,
  { isPreview, id }: { isPreview?: boolean; id?: string },
): StoredWidget {
  let result: StoredWidget | undefined;

  if (isPreview) {
    result = state.previewWidget;
  } else if (id) {
    const widgetIndexLookup = getWidgetIndexLookup(state.widgets);
    const index = widgetIndexLookup[id];
    result = state.widgets[index];
  }

  if (!result) {
    throw new Error(`Can't find widget to update ${(id ?? isPreview) ? 'preview' : '???'}`);
  }

  return result;
}

function getTickerDataToUpdate(state: Draft<HomePageState>, isPreview: boolean): TickerData {
  return isPreview ? state.previewTickerData : state.tickerData;
}
